import { Link } from 'react-router-dom';
import { z } from './gemeinsam';
import {
  ContentSection, EmptyState, ErrorState, KpiCard, KpiReihe, StatusBadge,
} from '../../muster';

interface Modell {
  id: string;
  name: string;
  isActive?: boolean;
  revitVersion: number;
  rollen?: string[];
}

/**
 * Aufnahme und Stand: die Werkzeuge, die VOR dem Zeichnen helfen, die
 * Koordinationsaufnahme je Gewerkemodell und die Kollisionssuche über den
 * Rasterindex.
 */
export function AufnahmeUndStand({
  projektId, ohneLauf, quellen, laeuft, onAufnehmen, meldung,
  suche, sucht, onSuchen,
}: {
  projektId: string;
  ohneLauf: boolean;
  quellen: Modell[];
  laeuft: string | null;
  onAufnehmen: (m: Modell) => void;
  meldung: string | null;
  suche: any;
  sucht: boolean;
  onSuchen: () => void;
}) {
  return (
    <>
      {/* ══════════════════════════════════════════════════════════════════
          DIE BEIDEN SEITEN, DIE VORHER HELFEN — GANZ OBEN

          Diese Seite meldet, was schiefgegangen ist. Die beiden Verweise
          führen zu dem, was vorher hilft: wo noch Platz ist, und wer welchen
          Korridor schon beansprucht hat.

          Ein Werkzeug, das nur hinterher meckert, wird geduldet. Eines, das
          vorher hilft, wird benutzt — also darf der Weg dorthin nicht unten
          stehen.
          ══════════════════════════════════════════════════════════════════ */}
      <ContentSection
        titel="Was vorher hilft"
        beschreibung={'Belegungsplan und Reservierungen arbeiten VOR dem Zeichnen. Was '
          + 'darunter steht, ist die Auswertung danach — ein Werkzeug, das nur '
          + 'hinterher meckert, wird geduldet; eines, das vorher hilft, wird benutzt.'}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'center',
                      flexWrap: 'wrap' }}>
          <Link to={`/projekte/${projektId}/koordination/belegung`}
                className="m-knopf m-knopf-haupt" style={{ textDecoration: 'none' }}>
            Belegungsplan — wo ist noch Platz
          </Link>

          <Link to={`/projekte/${projektId}/koordination/reservierungen`}
                className="m-knopf" style={{ textDecoration: 'none' }}>
            Reservierungen
          </Link>

          <Link to={`/projekte/${projektId}/koordination/nachtlauf`}
                className="m-knopf" style={{ textDecoration: 'none' }}>
            Nächtlicher Lauf
          </Link>

          <Link to={`/projekte/${projektId}/koordination/sammel`}
                className="m-knopf" style={{ textDecoration: 'none' }}>
            Sammelfunktionen
          </Link>

          <Link to={`/projekte/${projektId}/koordination/projektleitung`}
                className="m-knopf" style={{ textDecoration: 'none' }}>
            Projektleitung
          </Link>
        </div>
      </ContentSection>

      {/* ══════════════════════════════════════════════════════════════════
          DER STAND DER DINGE — zuerst und ohne Beschönigung

          Ohne Aufnahme gibt es keine Geometrie. Das ist keine Kleinigkeit,
          die man unten in eine Fußnote schreibt: Die Gewerketabellen führen
          keine Bounding Box, und ohne sie ist jede Kollisionszahl erfunden.
          ══════════════════════════════════════════════════════════════════ */}
      {ohneLauf && (
        <>
          <ErrorState
            titel="Noch kein Kollisionslauf"
            meldung={'Die Grundlagen stehen — Bereiche, Regeln, Toleranzen. Was fehlt, '
              + 'ist die GEOMETRIE: Keine Gewerketabelle führt eine Bounding Box.'}
          />

          <p className="schwach" style={{ fontSize: 13 }}>
            Leitungen tragen einen Punkt und eine Länge — keine Achse, keine
            Richtung. Ein 12-m-Kanal landet damit in <em>einer</em> Rasterzelle
            statt in sechs, und zwar an der Stelle seines Mittelpunkts; genau
            die Kollisionen an seinen Enden fänden nie statt.
          </p>

          <p className="schwach" style={{ fontSize: 13 }}>
            Über die Verbindungspunkte ließe sich die Achse ableiten, aber das
            trägt nicht: Von 40.774 Heizungsrohren haben 16.162 zwei
            Verbindungspunkte (40 %), 17.301 gar keinen. Ein Index, der bei
            sechs von zehn Bauteilen rät, ist keiner. Die Aufnahme liest Achse
            und Bounding Box deshalb selbst aus Revit.
          </p>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          DIE AUFNAHME STARTEN — ein Auftrag je Gewerkemodell
          ══════════════════════════════════════════════════════════════════ */}
      <ContentSection
        titel="Koordinationsaufnahme starten"
        beschreibung={'Ein Auftrag JE GEWERKEMODELL — jedes Fachmodell ist ein eigenes '
          + 'Revit-Dokument. Jeder Lauf bekommt seine eigene Auftragsnummer und liest '
          + 'sein Modell vollständig; die Fremdmodelle (Architektur, Tragwerk, Küche, '
          + 'Medientechnik) liest jeder Lauf mit, weil sie die Begrenzung bilden. Alle '
          + 'Läufe eines Tages ergänzen DENSELBEN Stand — liefen sie in getrennte '
          + 'Stände, könnte Lüftung nie mit Heizung kollidieren.'}
      >
        {quellen.length === 0 ? (
          <EmptyState
            titel="Kein Modell mit einer Quellrolle zugeordnet."
            erklaerung={'Ohne Zuordnung ist nicht bestimmt, welches Modell welches '
              + 'Gewerk führt. Die Zuordnung steht unter Einstellungen.'}
          />
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {quellen.map((m) => (
              <button key={m.id} className="m-knopf" disabled={laeuft !== null}
                      onClick={() => onAufnehmen(m)}
                      title={`Aufnahme in ${m.name} — Revit `
                           + `${m.revitVersion}, Rollen: `
                           + `${(m.rollen ?? []).join(', ')}`}>
                {laeuft === m.id ? 'wird angelegt …' : m.name}
              </button>
            ))}
          </div>
        )}

        {meldung && <div className="hinweis">{meldung}</div>}
      </ContentSection>

      {/* ══════════════════════════════════════════════════════════════════
          DIE SUCHE — ueber den Rasterindex, nicht ueber alle Paare
          ══════════════════════════════════════════════════════════════════ */}
      <ContentSection
        titel="Kollisionssuche"
        beschreibung={'Die Suche läuft über den RASTERINDEX und nicht über alle Paare: '
          + '173.639 Bauteile ergäben 15 Milliarden Vergleiche. Zwei Bauteile können '
          + 'sich nur durchdringen, wenn sie mindestens eine Rasterzelle teilen — das '
          + 'verwirft kein Paar, das sich überlappt.'}
        aktionen={(
          <button onClick={onSuchen} disabled={sucht} className="m-knopf m-knopf-haupt">
            {sucht ? 'sucht …' : 'Suche starten'}
          </button>
        )}
      >
        {!suche && (
          <EmptyState
            titel="Noch keine Suche gelaufen."
            erklaerung={'Sie braucht eine Koordinationsaufnahme als Grundlage — ohne '
              + 'Geometrie gibt es nichts zu vergleichen.'}
          />
        )}

        {suche && (
          <>
            <KpiReihe>
              <KpiCard titel="Durchdringungen" wert={suche.kollisionen} />
              <KpiCard titel="Geprüfte Paare" wert={suche.geprueftePaare} />
              <KpiCard titel="Bauteile" wert={suche.bauteile} />
              <KpiCard titel="Belegte Zellen" wert={suche.zellen} />
            </KpiReihe>

            {/* --- Die Einstufungen ------------------------------------- */}
            {suche.jeEinstufung && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                  Wie sie eingestuft sind
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.entries(suche.jeEinstufung as Record<string, number>)
                    .sort((a2, b2) => b2[1] - a2[1])
                    .map(([kind, n]) => (
                      <StatusBadge
                        key={kind}
                        klein
                        zustand={kind === 'echt' ? 'fehler' : 'neutral'}
                        text={`${z(n)} ${kind}`}
                      />
                    ))}
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════
                DIE GEGENPROBEN
                ══════════════════════════════════════════════════════════

                Eine Zahl, die niemand nachgerechnet hat, ist eine
                Behauptung. Diese Proben pruefen die Suche gegen sich
                selbst — sie stehen HIER und nicht im Protokoll, denn im
                Protokoll liest sie niemand. */}
            {(suche.gegenproben ?? []).length > 0 && (
              <div style={{ marginTop: 14, paddingTop: 12,
                            borderTop: '1px solid var(--linie)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                  Gegenproben
                </div>

                {(suche.gegenproben ?? []).map((g: string, i: number) => (
                  <div key={i} className="schwach"
                       style={{ fontSize: 12, marginBottom: 5 }}>
                    {g}
                  </div>
                ))}
              </div>
            )}

            {(suche.hinweise ?? []).map((h: string, i: number) => (
              <div key={i} className="hinweis">{h}</div>
            ))}
          </>
        )}
      </ContentSection>
    </>
  );
}
